import { symbolsMatchForChart } from './tradingChartPositionOverlays';

const ENTRY_COLORS = {
  BUY: '#22C55E',
  SELL: '#EF4444',
  UNKNOWN: '#F59E0B',
};

const EXIT_COLOR = '#38BDF8';
const PROFIT_COLOR = '#22C55E';
const LOSS_COLOR = '#EF4444';
const FLAT_COLOR = '#94A3B8';

function normalizeTimeSeconds(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return value > 1000000000000 ? Math.floor(value / 1000) : value;
  }
  const ms = Date.parse(value);
  if (!Number.isNaN(ms)) return Math.floor(ms / 1000);
  const parsed = Number(value);
  if (!Number.isNaN(parsed)) return normalizeTimeSeconds(parsed);
  return null;
}

function formatPrice(value) {
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return value.trim();
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  return parsed.toLocaleString(undefined, {
    useGrouping: false,
    maximumFractionDigits: 10,
  });
}

function formatPositivePrice(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return '';
  return formatPrice(value);
}

function parsePositivePrice(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatMoney(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  const sign = parsed > 0 ? '+' : '';
  return `${sign}${parsed.toFixed(2)}`;
}

function shortTradeId(trade) {
  const raw = trade?.position_id ?? trade?.deal_id ?? trade?.order_id ?? trade?.id;
  if (raw === undefined || raw === null || raw === '') return '';
  const value = String(raw);
  return value.length > 4 ? value.slice(-4) : value;
}

function getTradeSymbol(trade) {
  return trade?.instrument ?? trade?.symbol ?? trade?.instrument_symbol ?? '';
}

function normalizeDirection(trade) {
  const value = String(trade?.direction || trade?.side || '').trim().toUpperCase();
  if (value.includes('BUY') || value === 'LONG') return 'BUY';
  if (value.includes('SELL') || value === 'SHORT') return 'SELL';
  return 'UNKNOWN';
}

function getBarRealTime(bar) {
  return normalizeTimeSeconds(bar?.time_real ?? bar?.time);
}

function getBarChartTime(bar) {
  return normalizeTimeSeconds(bar?.time);
}

function findLoadedBarByChartTime(time, bars = []) {
  const target = normalizeTimeSeconds(time);
  if (target === null || !Array.isArray(bars)) return null;
  return bars.find((bar) => getBarChartTime(bar) === target) || null;
}

function firstPositivePrice(source, keys) {
  for (const key of keys) {
    const price = parsePositivePrice(source?.[key]);
    if (price !== null) {
      return {
        value: price,
        text: formatPositivePrice(source[key]),
        estimated: false,
      };
    }
  }
  return null;
}

function resolveClosePrice(trade, exitTime, bars = []) {
  const explicit = firstPositivePrice(trade, [
    'close_price',
    'exit_price',
    'price_close',
    'closePrice',
    'exitPrice',
    'closed_price',
  ]);
  if (explicit) return explicit;

  const exitBar = findLoadedBarByChartTime(exitTime, bars);
  const barClose = parsePositivePrice(exitBar?.close);
  if (barClose !== null) {
    return {
      value: barClose,
      text: formatPrice(barClose),
      estimated: true,
    };
  }

  return null;
}

function medianInterval(values) {
  const intervals = [];
  for (let i = 1; i < values.length; i += 1) {
    const diff = values[i] - values[i - 1];
    if (diff > 0) intervals.push(diff);
  }
  if (!intervals.length) return 86400;
  intervals.sort((a, b) => a - b);
  return intervals[Math.floor(intervals.length / 2)];
}

export function snapTradeTimeToLoadedBar(time, bars = []) {
  const target = normalizeTimeSeconds(time);
  if (target === null || !Array.isArray(bars) || bars.length === 0) return null;

  const indexedBars = bars
    .map((bar) => ({
      realTime: getBarRealTime(bar),
      chartTime: getBarChartTime(bar),
    }))
    .filter((bar) => bar.realTime !== null && bar.chartTime !== null)
    .sort((a, b) => a.realTime - b.realTime);

  if (!indexedBars.length) return null;

  const realTimes = indexedBars.map((bar) => bar.realTime);
  const interval = medianInterval(realTimes);
  const first = indexedBars[0];
  if (target < first.realTime) {
    return first.realTime - target <= interval * 1.5 ? first.chartTime : null;
  }

  for (let i = 1; i < indexedBars.length; i += 1) {
    if (indexedBars[i].realTime > target) return indexedBars[i - 1].chartTime;
  }

  const last = indexedBars[indexedBars.length - 1];
  return target - last.realTime <= interval * 1.5 ? last.chartTime : null;
}

export function isRealClosedHistoricalTrade(trade, selectedSymbol, selectedAccount) {
  if (!trade || typeof trade !== 'object') return false;
  if (selectedAccount && trade.account_id && String(trade.account_id) !== String(selectedAccount)) return false;
  if (!symbolsMatchForChart(getTradeSymbol(trade), selectedSymbol)) return false;
  if (String(trade.trade_status || '').toLowerCase() !== 'closed') return false;
  if (String(trade.source || '').toUpperCase() === 'BACKTEST') return false;
  if (normalizeTimeSeconds(trade.created_at) === null) return false;
  if (normalizeTimeSeconds(trade.closed_at) === null) return false;
  if (!Number.isFinite(Number(trade.entry_price)) || Number(trade.entry_price) <= 0) return false;
  return true;
}

function buildEntryText(item) {
  const side = item.direction === 'SELL' ? 'SELL' : item.direction === 'BUY' ? 'BUY' : 'TRADE';
  const parts = [`${side}${item.idSuffix}`];
  if (item.entryPriceText) parts.push(`@ ${item.entryPriceText}`);
  return parts.join(' ');
}

function buildExitText(item) {
  const parts = [`EXIT${item.idSuffix}`];
  if (item.closePriceText) parts.push(`@ ${item.closePriceEstimated ? '~' : ''}${item.closePriceText}`);
  if (!item.hasConnector && item.direction !== 'UNKNOWN' && item.entryPriceText) {
    parts.push(`from ${item.direction} ${item.entryPriceText}`);
  }
  if (item.pnlText) parts.push(item.pnlText);
  return parts.join(' ');
}

function buildRoundTripText(item) {
  const side = item.direction === 'SELL' ? 'SELL' : item.direction === 'BUY' ? 'BUY' : 'TRADE';
  const parts = [`${side} -> EXIT${item.idSuffix}`];
  if (item.entryPriceText && item.closePriceText) {
    parts.push(`${item.entryPriceText}->${item.closePriceEstimated ? '~' : ''}${item.closePriceText}`);
  } else if (item.closePriceText) {
    parts.push(`@ ${item.closePriceEstimated ? '~' : ''}${item.closePriceText}`);
  }
  if (item.pnlText) parts.push(item.pnlText);
  return parts.join(' ');
}

function buildGroupedEntryText(items) {
  const buys = items.filter((item) => item.direction === 'BUY').length;
  const sells = items.filter((item) => item.direction === 'SELL').length;
  const priceText = items[0]?.entryPriceText ? ` @ ${items[0].entryPriceText}` : '';
  if (buys && sells) return `${items.length} entries (${buys} BUY, ${sells} SELL)${priceText}`;
  if (buys) return `${items.length} BUY entries${priceText}`;
  if (sells) return `${items.length} SELL entries${priceText}`;
  return `${items.length} entries${priceText}`;
}

function buildGroupedExitText(items) {
  const netPnl = items.reduce((sum, item) => (
    Number.isFinite(item.pnlValue) ? sum + item.pnlValue : sum
  ), 0);
  const pnlText = formatMoney(netPnl);
  const priceText = items[0]?.closePriceText
    ? ` @ ${items[0].closePriceEstimated ? '~' : ''}${items[0].closePriceText}`
    : '';
  return `${items.length} exits${priceText} ${pnlText}`.trim();
}

function buildGroupedRoundTripText(items) {
  const buys = items.filter((item) => item.direction === 'BUY').length;
  const sells = items.filter((item) => item.direction === 'SELL').length;
  const netPnl = items.reduce((sum, item) => (
    Number.isFinite(item.pnlValue) ? sum + item.pnlValue : sum
  ), 0);
  const pnlText = formatMoney(netPnl);
  if (buys && sells) return `${items.length} round trips (${buys} BUY, ${sells} SELL) ${pnlText}`.trim();
  if (buys) return `${items.length} BUY -> EXIT ${pnlText}`.trim();
  if (sells) return `${items.length} SELL -> EXIT ${pnlText}`.trim();
  return `${items.length} round trips ${pnlText}`.trim();
}

function groupMarkerItems(items) {
  const groups = new Map();
  items.forEach((item) => {
    const bucketDirection = item.kind === 'entry' || item.kind === 'roundTrip' ? item.direction : 'EXIT';
    const priceKey = item.kind === 'entry' ? item.entryPriceText : item.closePriceText;
    const key = `${item.time}:${item.kind}:${bucketDirection}:${priceKey}`;
    const group = groups.get(key) || { key, items: [] };
    group.items.push(item);
    groups.set(key, group);
  });

  return Array.from(groups.values())
    .map(({ key, items: group }) => {
      const first = group[0];
      const isEntry = first.kind === 'entry';
      const isRoundTrip = first.kind === 'roundTrip';
      const isBuy = first.direction === 'BUY';
      const single = group.length === 1;
      return {
        id: single ? `${first.kind}${first.idSuffix || ''}:${first.time}` : `group:${key}`,
        time: first.time,
        position: isEntry
          ? (isBuy ? 'atPriceBottom' : 'atPriceTop')
          : 'atPriceMiddle',
        price: isEntry ? first.entryPriceValue : first.closePriceValue,
        color: isEntry
          ? (ENTRY_COLORS[first.direction] || ENTRY_COLORS.UNKNOWN)
          : (isRoundTrip ? connectorColor(first.pnlValue) : EXIT_COLOR),
        shape: isEntry
          ? (isBuy ? 'arrowUp' : 'arrowDown')
          : 'square',
        text: single
          ? (isEntry ? buildEntryText(first) : (isRoundTrip ? buildRoundTripText(first) : buildExitText(first)))
          : (isEntry ? buildGroupedEntryText(group) : (isRoundTrip ? buildGroupedRoundTripText(group) : buildGroupedExitText(group))),
      };
    })
    .sort((a, b) => {
      if (a.time !== b.time) return a.time - b.time;
      const aIsExit = a.text.startsWith('EXIT') || a.text.includes(' exits');
      const bIsExit = b.text.startsWith('EXIT') || b.text.includes(' exits');
      if (aIsExit === bIsExit) return 0;
      return aIsExit ? 1 : -1;
    });
}

function connectorColor(pnlValue) {
  if (!Number.isFinite(pnlValue) || pnlValue === 0) return FLAT_COLOR;
  return pnlValue > 0 ? PROFIT_COLOR : LOSS_COLOR;
}

export function buildHistoricalTradeOverlay(trades, selectedSymbol, bars, selectedAccount, options = {}) {
  if (!Array.isArray(trades)) return { markers: [], connectors: [] };

  const maxTrades = Number.isFinite(Number(options.maxTrades ?? options.maxMarkers))
    ? Math.max(1, Number(options.maxTrades ?? options.maxMarkers))
    : 16;
  const pairs = [];

  trades.forEach((trade) => {
    if (!isRealClosedHistoricalTrade(trade, selectedSymbol, selectedAccount)) return;

    const direction = normalizeDirection(trade);
    const isBuy = direction === 'BUY';
    const id = shortTradeId(trade);
    const idSuffix = id ? ` #${id}` : '';
    const entryPriceValue = parsePositivePrice(trade.entry_price);
    const entryPriceText = formatPositivePrice(trade.entry_price);
    const pnlValue = Number(trade.actual_profit_loss);
    const pnlText = formatMoney(trade.actual_profit_loss);
    const entryTime = snapTradeTimeToLoadedBar(trade.created_at, bars);
    const exitTime = snapTradeTimeToLoadedBar(trade.closed_at, bars);
    if (exitTime === null) return;
    const closePrice = resolveClosePrice(trade, exitTime, bars);
    if (entryPriceValue === null || !closePrice) return;

    pairs.push({
      raw: trade,
      direction,
      isBuy,
      idSuffix,
      entryTime,
      exitTime,
      entryPriceValue,
      closePriceValue: closePrice.value,
      entryPriceText,
      closePriceText: closePrice.text,
      closePriceEstimated: closePrice.estimated,
      pnlValue,
      pnlText,
    });
  });

  const selectedPairs = pairs
    .sort((a, b) => a.exitTime - b.exitTime)
    .slice(-maxTrades);

  const markerItems = [];
  const connectors = [];

  selectedPairs.forEach((pair) => {
    const hasConnector = pair.entryTime !== null && pair.entryTime !== pair.exitTime;
    const markerPair = { ...pair, hasConnector };

    if (!hasConnector) {
      markerItems.push({ ...markerPair, kind: 'roundTrip', time: pair.exitTime });
      return;
    }

    if (pair.entryTime !== null) {
      markerItems.push({ ...markerPair, kind: 'entry', time: pair.entryTime });
    }

    markerItems.push({ ...markerPair, kind: 'exit', time: pair.exitTime });

    const from = { time: pair.entryTime, value: pair.entryPriceValue };
    const to = { time: pair.exitTime, value: pair.closePriceValue };
    connectors.push({
      id: pair.idSuffix || `${pair.entryTime}:${pair.exitTime}:${pair.direction}`,
      color: connectorColor(pair.pnlValue),
      data: from.time < to.time ? [from, to] : [to, from],
    });
  });

  return {
    markers: groupMarkerItems(markerItems),
    connectors,
  };
}

export function buildHistoricalTradeMarkers(trades, selectedSymbol, bars, selectedAccount, options = {}) {
  return buildHistoricalTradeOverlay(trades, selectedSymbol, bars, selectedAccount, options).markers;
}
